import axios from "axios";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { el } from "date-fns/locale";
registerLocale("el", el);

import { useNavigate } from "react-router-dom";
import { addOrganization } from "@/api/organizationApi";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/components/ui/tabs";

import useAxiosPrivate, {
	toastErrorUnlessHandled,
} from "@/hooks/useAxiosPrivate";
import { clampNumberInput, lengthTypesMap, MAX_NUMBER_INPUT, normalizeNumberInputDisplay, validateAFM } from "@/utils";
import { UserAddIcon } from "@heroicons/react/outline";
import {
	Button,
	Divider,
	NumberInput,
	Select,
	SelectItem,
	TextInput,
} from "@tremor/react";
import { addDays, addMonths, addYears, format } from "date-fns";
import { useEffect, useState } from "react";

import type { Package, Subscription } from "@/types";
import ModulesTab from "@/components/ModulesTab";
import { getAllFeatures, type Feature } from "@/api/featuresApi";
import { toast } from "sonner";

const RequiredLabel = ({ children }) => (
	<span>
		{children} <span className="text-red-500">*</span>
	</span>
);

interface Product {
	id: string;
	name: string;
}

interface Organization {
	id: string;
	name: string;
	afm: string;
	contact_email: string;
	phone_number: string;
	customer_s1: string;
	address: string;
}

const AddOrganization = ({
	getOrgs,
	orgs,
}: {
	getOrgs: (
		filters?: { from?: string; to?: string }
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	) => Promise<any>;
	orgs: Organization[];
}) => {
	const navigate = useNavigate();
	const [organization, setOrganization] = useState("");
	const [selectedOrganization, setSelectedOrganization] = useState("");
	const [afm, setAfm] = useState("");
	const [afmError, setAfmError] = useState("");
	const [softoneCode, setSoftoneCode] = useState("");
	const [email, setEmail] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [telNumber, setTelNumber] = useState("");
	const [telNumberError, setTelNumberError] = useState("");
	const [address, setAddress] = useState("");
	const [allowedUsers, setAllowedUsers] = useState(1);
	const [allowedCreditsNumber, setAllowedCreditsNumber] = useState(0);
	const [products, setProducts] = useState<Product[]>();
	const [product, setProduct] = useState("");
	const [allowedCredits, setAllowedCredits] = useState(0);
	const [subscriptionLength, setSubscriptionLength] = useState("");
	const [subscriptionQuantity, setSubscriptionQuantity] = useState(1);
	const [startDate, setStartDate] = useState<string>(
		new Date().toISOString().split("T")[0],
	);
	const [endDate, setEndDate] = useState<string>("");
	const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);

	const [packages, setPackages] = useState<Package[]>();
	const [selectedPackage, setSelectedPackage] = useState("");

	const [dateRange] = useState({
		from: new Date(),
		to: new Date(new Date().setDate(new Date().getDate() + 15) - 86400000), // return new Date with one year added and one day subtracted
	});
	const [note, setNote] = useState("");
	const [notifyCustomer, setNotifyCustomer] = useState(true);
	const [clientId, setClientId] = useState("");
	const [clientIdError, setClientIdError] = useState("");
	const [popOpen, setPopOpen] = useState(false);
	const [availableFeatures, setAvailableFeatures] = useState<Feature[]>([]);
	const [selectedFeatureKeys, setSelectedFeatureKeys] = useState<string[]>([]);

	const axiosPrivate = useAxiosPrivate();

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();

		getAllFeatures(controller.signal)
			.then((response) => {
				if (isMounted) {
					setAvailableFeatures(response.data.data);
				}
			})
			.catch((error) => {
				console.error("Error fetching available features:", error);
				toastErrorUnlessHandled(
					error,
					"Σφάλμα κατά τη φόρτωση των διαθέσιμων επεκτάσεων",
				);
			});

		return () => {
			isMounted = false;
			controller.abort();
		};
	}, []);

	const validateAndFormatTelNumber = (value: string) => {
		if (value === "") {
			setTelNumberError("");
			return value;
		}
		// Remove all non-digit characters
		const digitsOnly = value.replace(/\D/g, "");
		return digitsOnly;
	};

	useEffect(() => {
		const org = orgs?.find((item) => item.id.toString() === selectedOrganization);
		setAfm(org?.afm || "");
		setContactEmail(org?.contact_email || "");
		setTelNumber(org?.phone_number || "");
		setSoftoneCode(org?.customer_s1 || "");
		setAddress(org?.address || "");
		setTelNumberError("");
		setAfmError("");
	}, [selectedOrganization]);

	useEffect(() => {
		setProduct(products?.[0] ? products[0].id.toString() : "");
	}, [products]);

	useEffect(() => {

		setAllowedCredits(
			packages?.find((item) => item.id.toString() === selectedPackage)?.details
				.allowed_credits || 0,
		);
	}, [selectedPackage]);

	useEffect(() => {
		if (startDate && subscriptionLength) {
			let newEndDate = new Date(startDate);

			switch (subscriptionLength) {
				case "year":
					newEndDate = addYears(newEndDate, subscriptionQuantity);
					break;
				case "month":
					newEndDate = addMonths(newEndDate, subscriptionQuantity);
					break;
				case "demo":
					newEndDate = addDays(newEndDate, 15);
					break;
				case "open":
					// For 'open' subscription, we don't set an end date
					setEndDate("");
					return;
			}

			// Subtract one day to get the last day of the subscription
			newEndDate = addDays(newEndDate, -1);
			setEndDate(format(newEndDate, "yyyy-MM-dd"));
		}
	}, [subscriptionLength, startDate, subscriptionQuantity]);

	useEffect(() => {
		const newSubscriptions: Subscription[] = [];

		const subscriptionObj = {
			package_id: selectedPackage,
			start_date: startDate,
			end_date: endDate,
			allowed_credits: allowedCredits,
			allowed_users: allowedUsers,
		};
		newSubscriptions.push(subscriptionObj);

		setSubscriptions(newSubscriptions);
	}, [
		subscriptionQuantity,
		selectedPackage,
		startDate,
		endDate,
		allowedCredits,
		allowedUsers,
		subscriptionLength,
	]);

	useEffect(() => {
		setAllowedCreditsNumber(allowedCredits);
	}, [allowedCredits]);

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();

		if (product) {
			getPackagesForProduct(isMounted, controller);
		}

		return () => {
			isMounted = false;
			controller.abort();
		};
	}, [product]);

	async function handleSubmit() {
		if (afm && !validateAFM(afm)) {
			setAfmError("Το ΑΦΜ δεν είναι έγκυρο");
			return;
		}

		try {
			const response = await addOrganization(
				{
					email,
					contactEmail,
					telNumber,
					afm,
					softoneCode,
					address,
					organization: organization.trim(),
					allowedCreditsNumber,
					dateRange,
					product,
					subscriptions,
					note: note.trim(),
					clientId,
					featureKeys: selectedFeatureKeys,
					notifyCustomer,
				},
			);

			await getOrgs();
			setPopOpen(false);
			setEmail("");
			setContactEmail("");
			setTelNumber("");
			setSoftoneCode("");
			setAfm("");
			setAddress("");
			setOrganization("");
			setSelectedPackage("");
			setProduct("");
			setProducts([]);
			setAllowedUsers(1);
			setAllowedCreditsNumber(0);
			setSelectedOrganization("");
			setClientId("");
			setClientIdError("");
			setSelectedFeatureKeys([]);
			setNotifyCustomer(true);

			toast.success("Δημιουργήθηκε ο οργανισμός!");
			if (response.data?.auto_paused_users_count > 0) {
				toast.warning(
					`${response.data.auto_paused_users_count} χρήστες τέθηκαν αυτόματα σε παύση λόγω του νέου ορίου.`,
				);
			}
			if (response.data?.email_warning) {
				toast.warning(response.data.email_warning);
			}
			const newOrganizationId = response.data?.organization?.[0]?.id;
			if (newOrganizationId) {
				navigate(`/org/${newOrganizationId}`);
			}
		} catch (error) {
			if (axios.isAxiosError(error)) {
				if (error.response) {
					// The request was made and the server responded with a status code
					// that falls out of the range of 2xx
					toastErrorUnlessHandled(
						error,
						error.response.data || "Σφάλμα κατά τη δημιουργία του οργανισμού",
					);
				} else if (error.request) {
					// The request was made but no response was received
					toastErrorUnlessHandled(
						error,
						"Δεν ελήφθη απάντηση από τον διακομιστή",
					);
				} else {
					// Something happened in setting up the request that triggered an Error
					toastErrorUnlessHandled(
						error,
						"Σφάλμα κατά την αποστολή του αιτήματος",
					);
				}
			} else {
				// Handle non-Axios errors
				toastErrorUnlessHandled(error, "Απροσδόκητο σφάλμα");
			}
		}
	}

	useEffect(() => {
		const getdata = async () => {
			try {
				const response = await axiosPrivate.get(`/auth/products`);

				if (response.data.products) {
					setProducts(response.data.products);
				}
			} catch (error) {
				console.error(error);
				//navigate("/login", { state: { from: location }, replace: true });
			}
		};

		getdata();
	}, []);

	async function getPackagesForProduct(
		isMounted = true,
		controller = new AbortController(),
	) {
		try {
			const response = await axiosPrivate.get(
				`/auth/packages/product/${Number.parseInt(product!)}`,
				{
					signal: controller.signal,
				},
			);
			response.data.packages.sort(
				(
					a: { details: { allowed_credits: number } },
					b: { details: { allowed_credits: number } },
				) => a.details.allowed_credits - b.details.allowed_credits,
			);
			isMounted && setPackages(response.data.packages);
		} catch (error) {
			console.error(error);
			//navigate("/login", { state: { from: location }, replace: true });
		}
	}
	return (
		<>
			<Button
				className="ml-2"
				onClick={() => {
					setPopOpen(true);
				}}
			>
				Προσθήκη
			</Button>

			<Dialog
				open={popOpen}
				onOpenChange={() => {
					setPopOpen(!popOpen);
					// clear form fields and error messages
					setOrganization("");
					setSelectedOrganization("");
					setAfm("");
					setSoftoneCode("");
					setEmail("");
					setContactEmail("");
					setTelNumber("");
					setAddress("");
					setProduct("");
					setSelectedPackage("");
					setAllowedCredits(0);
					setAllowedUsers(1);
					setSubscriptionLength("");
					setSubscriptionQuantity(1);
					setStartDate(new Date().toISOString().split("T")[0]);
					setEndDate("");
					setNote("");
					setNotifyCustomer(true);
					setClientId("");
					setClientIdError("");
					setSelectedFeatureKeys([]);
					setAfmError("");
					setTelNumberError("");
				}}
			>
				<DialogTrigger
				// onClick={() => {
				// 	setPopOpen(!popOpen);
				// }}
				></DialogTrigger>
				<DialogContent
					className="w-[600px] h-[700px] max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden"
					onPointerDownOutside={(e) => {
						e.preventDefault();
					}}
				>
					<DialogHeader>
						<DialogTitle>Προσθήκη Οργανισμού</DialogTitle>
					</DialogHeader>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							handleSubmit();
						}}
						className="flex flex-col flex-1 min-h-0 gap-4"
					>
						<Tabs defaultValue="org" className="flex flex-col flex-1 min-h-0">
							<TabsList className="w-full shrink-0">
								<TabsTrigger value="org" className="flex-1">
									Στοιχεία Οργανισμού
								</TabsTrigger>
								<TabsTrigger value="sub" className="flex-1">
									Στοιχεία Συνδρομής
								</TabsTrigger>
								<TabsTrigger value="ext" className="flex-1">
									Επεκτάσεις
								</TabsTrigger>
							</TabsList>

							<TabsContent value="org" className="space-y-4 mt-4 flex-1 overflow-y-auto pr-1">
								<div className="grid grid-cols-1 gap-4">
									<div>
										<label
											htmlFor="organization"
											className="block text-sm font-medium text-gray-700"
										>
											<RequiredLabel>Οργανισμός</RequiredLabel>
										</label>
										<TextInput
											id="organization"
											placeholder="Ονομασία Οργανισμού"
											value={organization}
											onValueChange={setOrganization}
											required
										/>
									</div>
									<div>
										<label
											htmlFor="existingOrg"
											className="block text-sm font-medium text-gray-700"
										>
											Αντιγραφή Στοιχείων από Υπάρχοντα Οργανισμό
										</label>
										<Select
											id="existingOrg"
											value={selectedOrganization}
											onValueChange={setSelectedOrganization}
											enableClear={true}
											placeholder="Επιλέξτε"
										>
											<SelectItem value="">-</SelectItem>
											{orgs?.map((org) => (
												<SelectItem
													key={org.id.toString()}
													value={org.id.toString()}
												>
													{org.name}
												</SelectItem>
											))}
										</Select>
									</div>
								</div>

								<div className="grid grid-cols-2 gap-4">
									<div>
										<label
											htmlFor="contactEmail"
											className="block text-sm font-medium text-gray-700"
										>
											<RequiredLabel>Email Επικοινωνίας</RequiredLabel>
										</label>
										<TextInput
											id="contactEmail"
											type="email"
											placeholder="Email Επικοινωνίας"
											value={contactEmail}
											onValueChange={(value) => setContactEmail(value.trim())}
											required
										/>
									</div>
									<div>
										<label
											htmlFor="telNumber"
											className="block text-sm font-medium text-gray-700"
										>
											<RequiredLabel>Τηλέφωνο</RequiredLabel>
										</label>
										<TextInput
											id="telNumber"
											placeholder="Τηλ. Επικοινωνίας"
											value={telNumber}
											onValueChange={(value) => {
												const formattedValue = validateAndFormatTelNumber(value);
												setTelNumber(formattedValue);
											}}
											error={telNumberError !== ""}
											errorMessage={telNumberError}
											required
										/>
									</div>
								</div>

								<div className="grid grid-cols-2 gap-4">
									<div>
										<label
											htmlFor="afm"
											className="block text-sm font-medium text-gray-700"
										>
											ΑΦΜ
										</label>
										<TextInput
											id="afm"
											placeholder="ΑΦΜ"
											value={afm}
											error={afmError !== ""}
											errorMessage={afmError}
											onValueChange={(value) => {
												if (value) {
													setAfm(value);
													setAfmError(
														validateAFM(value) ? "" : "Το ΑΦΜ δεν είναι έγκυρο",
													);
												} else {
													setAfm("");
													setAfmError("");
												}
											}}
										/>
									</div>
									<div>
										<label
											htmlFor="address"
											className="block text-sm font-medium text-gray-700"
										>
											Διεύθυνση
										</label>
										<TextInput
											id="address"
											placeholder="Διεύθυνση"
											value={address}
											onValueChange={setAddress}
										/>
									</div>
								</div>

								<div className="grid grid-cols-2 gap-4">
									<div>
										<label
											htmlFor="softoneCode"
											className="block text-sm font-medium text-gray-700"
										>
											Κωδ. SoftOne
										</label>
										<TextInput
											id="softoneCode"
											placeholder="Κωδ. SoftOne"
											value={softoneCode}
											onValueChange={setSoftoneCode}
										/>
									</div>
									<div>
										<label
											htmlFor="clientId"
											className="block text-sm font-medium text-gray-700"
										>
											<RequiredLabel>Client ID</RequiredLabel>

										</label>
										<TextInput
											id="clientId"
											placeholder="Client ID"
											value={clientId}
											onValueChange={(value) => {
												const trimmed = value.trimStart();
												if (trimmed === "") {
													setClientId("");
													setClientIdError("");
													return;
												}
												if (/[^a-zA-Z0-9]/.test(trimmed)) {
													setClientIdError("Χωρίς ειδικούς χαρακτήρες ή κενά");
												} else if (trimmed.length > 8) {
													setClientIdError("Μέγιστο 8 χαρακτήρες");
												} else {
													setClientIdError("");
												}
												setClientId(trimmed);
											}}
											error={clientIdError !== ""}
											errorMessage={clientIdError}
											required
											maxLength={8}
										/>
									</div>
								</div>

								<div className="grid grid-cols-2 gap-4">
									<div>
										<label
											htmlFor="email"
											className="block text-sm font-medium text-gray-700"
										>
											Email Admin του Οργανισμού
										</label>
										<TextInput
											id="email"
											type="email"
											placeholder="email"
											value={email}
											onValueChange={(value) => setEmail(value.trim())}
										/>
									</div>
								</div>
							</TabsContent>

							<TabsContent value="sub" className="space-y-4 mt-4 flex-1 overflow-y-auto pr-1">
								<div className="grid grid-cols-2 gap-4">
									<div>
										<label
											htmlFor="product"
											className="block text-sm font-medium text-gray-700"
										>
											<RequiredLabel>Προϊόν</RequiredLabel>
										</label>
										<Select id="product" value={product} onValueChange={setProduct}>
											{products?.map((prod) => (
												<SelectItem
													key={prod.id.toString()}
													value={prod.id.toString()}
												>
													{prod.name}
												</SelectItem>
											))}
										</Select>
									</div>
									<div>

										<RequiredLabel>Πακέτο</RequiredLabel>
										<Select
											id="subscriptionType"
											value={selectedPackage}
											onValueChange={setSelectedPackage}
											placeholder="Επιλέξτε"
										>
											{packages?.map((pkg) => (
												<SelectItem
													key={pkg.id.toString()}
													value={pkg.id.toString()}
												>
													{pkg.name}
												</SelectItem>
											))}
										</Select>
									</div>
								</div>

								<div className="grid grid-cols-2 gap-4">
									<div>

										<RequiredLabel>Επιτρεπόμενα Credits</RequiredLabel>

										<NumberInput
											id="allowedCredits"
											placeholder="Αριθμός Credits"
											value={allowedCredits}
											onValueChange={(value) => setAllowedCredits(clampNumberInput(value))}
											onChange={(event) => {
												event.target.value = normalizeNumberInputDisplay(event.target.value);
											}}
											min={0}
											max={MAX_NUMBER_INPUT}
										/>
									</div>
									<div>
										<label
											htmlFor="allowedUsers"
											className="block text-sm font-medium text-gray-700"
										>
											<RequiredLabel>Επιτρεπόμενοι Χρήστες</RequiredLabel>
										</label>
										<NumberInput
											id="allowedUsers"
											placeholder="Αριθμός Χρηστών"
											value={allowedUsers}
											onValueChange={(value) =>
												setAllowedUsers(clampNumberInput(value, { min: 1 }))
											}
											onChange={(event) => {
												event.target.value = normalizeNumberInputDisplay(
													event.target.value,
													{ min: 1 },
												);
											}}
											min={1}
											max={MAX_NUMBER_INPUT}
										/>
									</div>
								</div>

								<div className="grid grid-cols-2 gap-4">
									<div>
										<RequiredLabel>Διάρκεια Συνδρομής</RequiredLabel>

										<Select
											id="subscriptionLength"
											value={subscriptionLength}
											onValueChange={setSubscriptionLength}
											placeholder="Επιλέξτε"
										>
											{packages
												?.find((item) => item.id.toString() === selectedPackage)
												?.details?.length_type?.map((k) => (
													<SelectItem key={k} value={k}>
														{lengthTypesMap[k]}
													</SelectItem>
												))}
										</Select>
									</div>
									<div>

										<RequiredLabel>Ημερομηνία Έναρξης</RequiredLabel>
										<DatePicker
											selected={startDate ? new Date(startDate) : undefined}
											onChange={(date) => {
												setStartDate(date ? date.toISOString().split("T")[0] : "");
											}}
											locale="el"
											dateFormat="dd/MM/yyyy"
											placeholderText="Επιλέξτε Ημερομηνία"
											showYearDropdown
											yearDropdownItemNumber={40}
											scrollableYearDropdown
											className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
										/>
									</div>
								</div>

								{endDate && (
									<div className="grid grid-cols-2 gap-4">
										<div>
											<RequiredLabel>Ημερομηνία Λήξης</RequiredLabel>
											<DatePicker
												selected={endDate ? new Date(endDate) : undefined}
												locale="el"
												dateFormat="dd/MM/yyyy"
												placeholderText="Επιλέξτε Ημερομηνία"
												showYearDropdown
												yearDropdownItemNumber={40}
												scrollableYearDropdown
												readOnly
												className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											/>
										</div>
									</div>
								)}

								{endDate && (
									<div>

										<RequiredLabel>Ποσότητα Συνδρομών</RequiredLabel>

										<NumberInput
											id="subscriptionQuantity"
											placeholder="Ποσότητα"
											value={subscriptionQuantity}
											onValueChange={(value) =>
												setSubscriptionQuantity(clampNumberInput(value, { min: 1 }))
											}
											min={1}
											max={MAX_NUMBER_INPUT}
										/>
									</div>
								)}

								<div>
									<label
										htmlFor="subscriptionNote"
										className="block text-sm font-medium text-gray-700"
									>
										Σημείωση
									</label>
									<TextInput
										id="subscriptionNote"
										placeholder="Σημείωση"
										value={note}
										onValueChange={setNote}
									/>
								</div>
								<div className="flex items-center space-x-2">
									<Checkbox
										id="notifyCustomer"
										checked={notifyCustomer}
										onCheckedChange={(checked) =>
											setNotifyCustomer(checked === true)
										}
									/>
									<label
										htmlFor="notifyCustomer"
										className="text-sm font-medium text-gray-700"
									>
										Ενημέρωση πελάτη
									</label>
								</div>
							</TabsContent>

							<TabsContent value="ext" className="mt-4 flex-1 overflow-y-auto pr-1">
								<ModulesTab
									modules={availableFeatures}
									selectedKeys={selectedFeatureKeys}
									setSelectedKeys={setSelectedFeatureKeys}
									emptyText="Δεν υπάρχουν διαθέσιμες επεκτάσεις"
								/>
							</TabsContent>
						</Tabs>

						<Divider className="shrink-0" />

						<div className="flex justify-center space-x-4 shrink-0">
							<Button
								color="emerald"
								icon={UserAddIcon}
								disabled={
									clientId === "" ||
									clientIdError !== "" ||
									contactEmail === "" ||
									organization === "" ||
									telNumber === "" ||
									selectedPackage === "" ||
									subscriptionLength === "" ||
									endDate === ""
								}
								tooltip={
									organization === ""
										? "Η ονομασία είναι υποχρεωτική"
										: contactEmail === ""
											? "Το email είναι υποχρεωτικό"
											: telNumber === ""
												? "Το τηλέφωνο είναι υποχρεωτικό"
												: selectedPackage === ""
													? "Το πακέτο είναι υποχρεωτικό"
													: subscriptionLength === ""
														? "Η διάρκεια συνδρομής είναι υποχρεωτική"
														: endDate === ""
															? "Δεν έχουν συμπληρωθεί όλα τα πεδία της Συνδρομής"
															: ""
								}
								type="submit"
								className="w-2/3"
							>
								Προσθήκη
							</Button>
							<Button
								color="red"
								onClick={() => {
									setEmail("");
									setContactEmail("");
									setTelNumber("");
									setSoftoneCode("");
									setAfm("");
									setAddress("");
									setOrganization("");
									setAllowedUsers(1);
									setAllowedCreditsNumber(0);
									setClientId("");
									setClientIdError("");
									setNotifyCustomer(true);
									setPopOpen(false);
								}}
								className="w-1/3"
							>
								Ακύρωση
							</Button>
						</div>
					</form>
				</DialogContent>
			</Dialog>
			{/* </Button> */}
		</>
	);
};

export default AddOrganization;
